import { Button } from '@farol-ds/react'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import {
  HELP_CENTER_URL,
  ID_VALIDATION_EVENTS,
  JUSID_PLATFORM_WEB_AUTH,
  ROUTES,
} from '../../constants'
import { type CpfErrorSessionData, useSignupFlowOptional } from '../../contexts'
import type { AuthPageEventData } from '../../utils/event-data'
import { redirectTo } from '../../utils/redirect'
import { LoginCard } from '../login-card/login-card'
import styles from './cpf-not-verified.module.scss'

function formatBirthDateBr(isoDate: string): string {
  const [y, m, d] = isoDate.split('-')
  if (!y || !m || !d) return isoDate
  return `${d}/${m}/${y}`
}

export interface CpfNotVerifiedProps {
  eventData: AuthPageEventData
}

type IdValidationFailedMetricsPayload = {
  app_id: string
  feature_code?: string
  from_component: string
  jusid_platform: string
  type: 'CPFInvalid'
  email: string
  metadata?: {
    cpf?: string
    birthDate?: string
    button?: string
  }
}

function buildMetricsPayload(
  propsEventData: AuthPageEventData,
  session: CpfErrorSessionData | null
): IdValidationFailedMetricsPayload | null {
  const base = {
    app_id: propsEventData.appId ?? JUSID_PLATFORM_WEB_AUTH,
    feature_code: propsEventData.featureCode,
    from_component: propsEventData.fromComponent ?? 'SignUp',
    jusid_platform: JUSID_PLATFORM_WEB_AUTH,
    type: 'CPFInvalid' as const,
  }
  const email = session?.email ?? propsEventData.email
  if (!email) {
    return null
  }

  const metadata: NonNullable<IdValidationFailedMetricsPayload['metadata']> = {}
  if (session?.cpf) metadata.cpf = session.cpf
  if (session?.birthDateIso) metadata.birthDate = formatBirthDateBr(session.birthDateIso)

  return {
    ...base,
    email,
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
  }
}

export function CpfNotVerified({ eventData: propsEventData }: CpfNotVerifiedProps) {
  const router = useRouter()
  const signupFlowContext = useSignupFlowOptional()

  /** One-shot session + first-render props, aligned with AccountLimitReached stable ref pattern. */
  const stableRef = useRef<{
    metricsPayload: IdValidationFailedMetricsPayload | null
  } | null>(null)

  if (!stableRef.current) {
    const session = signupFlowContext?.takeCpfErrorSession() ?? null
    stableRef.current = {
      metricsPayload: buildMetricsPayload(propsEventData, session),
    }
  }

  const viewedSentRef = useRef(false)

  useEffect(() => {
    const payload = stableRef.current?.metricsPayload
    if (!payload) {
      redirectTo(ROUTES.SIGN_UP.CPF_BASED_SIGNUP)
      return
    }
    if (viewedSentRef.current) {
      return
    }
    viewedSentRef.current = true
    sendMetrics(ID_VALIDATION_EVENTS.FAILED_VIEWED, null, payload)
  }, [])

  const handleReviewClick = () => {
    const payload = stableRef.current?.metricsPayload
    if (payload) {
      sendMetrics(ID_VALIDATION_EVENTS.FAILED_SUBMITTED, null, {
        ...payload,
        metadata: {
          ...payload.metadata,
          button: 'ReviewInformation',
        },
      })
    }
    router.back()
  }

  const header = {
    title: 'Identidade não pôde ser verificada',
    className: styles.headerCompactSpacing,
    subtitle: {
      asChild: true,
      content: (
        <h2>
          Revise as informações fornecidas ou entre em{' '}
          <a
            href={HELP_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.supportLink}
          >
            contato com o suporte
          </a>
          .
        </h2>
      ),
    },
  }

  return (
    <LoginCard header={header} hideCloseButton>
      <Button type="button" kind="primary" onClick={handleReviewClick}>
        Revisar informações
      </Button>
    </LoginCard>
  )
}
